import React, { useState } from 'react';
import { Volume2, Play, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/UserService';
import { ttsService } from '../services/TtsService';
import {
  ASSISTANT_VOICES,
  DEFAULT_VOICE_KEY,
  getVoice,
  VOICE_PREVIEW_TEXT,
} from '../models/assistantVoices';

/**
 * Per-user VERA assistant voice preferences (rendered on the Profile page).
 * Saves immediately on change — independent of the profile edit form.
 */
export const AssistantVoiceSettings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [voiceKey, setVoiceKey] = useState<string>(user?.assistantVoice || DEFAULT_VOICE_KEY);
  const [enabled, setEnabled] = useState<boolean>(user?.assistantVoiceEnabled ?? false);
  const [saved, setSaved] = useState(false);
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  if (!user) return null;

  const persist = async (nextVoice: string, nextEnabled: boolean) => {
    try {
      await userService.updateUser(user.id, {
        assistantVoice: nextVoice,
        assistantVoiceEnabled: nextEnabled,
      });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Failed to save voice preference:', e);
    }
  };

  const selectVoice = (key: string) => {
    setVoiceKey(key);
    persist(key, enabled);
  };

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    persist(voiceKey, next);
  };

  const preview = async (key: string) => {
    setPreviewKey(key);
    try {
      await ttsService.speak(VOICE_PREVIEW_TEXT, getVoice(key).voiceId);
    } finally {
      setTimeout(() => setPreviewKey(null), 1200);
    }
  };

  const renderVoice = (v: (typeof ASSISTANT_VOICES)[number]) => {
    const active = v.key === voiceKey;
    return (
      <div
        key={v.key}
        onClick={() => selectVoice(v.key)}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') selectVoice(v.key);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3)',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
          backgroundColor: active ? 'var(--color-light-accent)' : 'var(--color-white)',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `2px solid ${active ? 'var(--color-primary)' : 'var(--color-gray-400)'}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {active && (
              <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: 'var(--color-primary)' }} />
            )}
          </span>
          <div>
            <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>{v.label}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>{v.description}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            preview(v.key);
          }}
          title={`Preview ${v.label}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 10px',
            background: 'transparent',
            border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
          }}
        >
          <Play size={13} /> {previewKey === v.key ? 'Playing…' : 'Preview'}
        </button>
      </div>
    );
  };

  return (
    <div className="card" style={{ marginTop: 'var(--spacing-6)' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <Volume2 size={18} /> VERA Assistant Voice
        </h2>
        {saved && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--color-success)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            <Check size={16} /> Saved
          </span>
        )}
      </div>

      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-4)' }}>
        Choose how VERA sounds when it reads replies aloud. Preview each voice, then pick your favorite.
      </p>

      {/* Speak toggle */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={enabled} onChange={toggleEnabled} />
        <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)' }}>
          Speak assistant replies aloud
        </span>
      </label>

      <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>{ASSISTANT_VOICES.map(renderVoice)}</div>
    </div>
  );
};

export default AssistantVoiceSettings;
